export interface SendReportRequest {
  email: string;
  pdfBase64: string;
  consent: boolean;
  consentTimestamp: string; // ISO 8601
  language: 'de' | 'en';
  providerCount: number;
  reportType?: 'cloud' | 'workplace';
}

export interface SendReportResponse {
  success: boolean;
  message?: string;
  errors?: string[];
}

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Sends the report request to the backend API.
 * POST to /send-report with 30-second timeout.
 */
export async function sendReport(request: SendReportRequest): Promise<SendReportResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30_000);

  try {
    const response = await fetch(`${API_URL}/send-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      return await response.json() as SendReportResponse;
    }

    // Handle specific HTTP error codes
    if (response.status === 400) {
      const body = await response.json().catch(() => null);
      return {
        success: false,
        message: body?.message || 'Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.',
        errors: body?.errors,
      };
    }

    if (response.status === 429) {
      return {
        success: false,
        message: 'Zu viele Anfragen. Bitte versuchen Sie es in einer Minute erneut.',
      };
    }

    // 500 or any other server error
    return {
      success: false,
      message: 'Beim Versand ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    };
  } catch (error: unknown) {
    clearTimeout(timeoutId);

    if (error instanceof DOMException && error.name === 'AbortError') {
      return {
        success: false,
        message: 'Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.',
      };
    }

    return {
      success: false,
      message: 'Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
    };
  }
}
